import { type FC, Fragment } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { REQUEST_TYPE_LABEL_KEY } from "@/partner/entities/assistant";
import { PagePaths } from "@/partner/shared/constants";
import type { SupportRequestTypeType } from "@/types/graphql/partner.generated";

export interface DashboardRequestRow {
  id: string;
  type: SupportRequestTypeType;
  createdAt: string;
  user?: { id: string; email?: string | null } | null;
}

export interface SupportRequestsDashboardCardProps {
  /** Ждущие ответа команды: OPEN и VIEWER_REPLIED (supportRequestsOpenCount). */
  activeCount: number;
  /** Ждущие ответа зрителя (WAITING_VIEWER). */
  waitingViewerCount: number;
  latest: DashboardRequestRow[];
}

/**
 * Карточка обращений зрителей на дашборде партнёра. Вся область содержимого
 * это CardActionArea со ссылкой в «Аудитория», «Обращения», как у карточки
 * неоплаченного счёта; кнопка под ней ведёт туда же. Без открытых обращений
 * карточка не исчезает, а показывает спокойное состояние «всё хорошо».
 */
export const SupportRequestsDashboardCard: FC<
  SupportRequestsDashboardCardProps
> = ({ activeCount, waitingViewerCount, latest }) => {
  const { t } = useTranslation();
  const hasOpen = activeCount > 0;

  return (
    <Card sx={{ height: "100%" }}>
      <CardActionArea component={RouterLink} to={PagePaths.audienceRequests}>
        <CardContent sx={{ px: 2, pb: 2, pt: 2 }}>
          <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start" }}>
            {hasOpen ? (
              <SupportAgentIcon color="error" />
            ) : (
              <CheckCircleOutlineIcon color="success" />
            )}
            <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t("requests.dashboardCard.title")}
              </Typography>
              {hasOpen ? (
                <>
                  <Typography variant="h5" component="p">
                    {t("requests.dashboardCard.activeCount", {
                      value: activeCount,
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("requests.dashboardCard.waitingViewer", {
                      value: waitingViewerCount,
                    })}
                  </Typography>
                  {latest.length > 0 && (
                    <List disablePadding dense>
                      {latest.map((request, index) => (
                        <Fragment key={request.id}>
                          {index > 0 && <Divider component="li" />}
                          <ListItem disableGutters>
                            <ListItemText
                              primary={t(REQUEST_TYPE_LABEL_KEY[request.type])}
                              secondary={request.user?.email}
                              slotProps={{
                                secondary: { noWrap: true },
                              }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1, whiteSpace: "nowrap" }}
                            >
                              {t("requests.dashboardCard.age", {
                                value: request.createdAt,
                              })}
                            </Typography>
                          </ListItem>
                        </Fragment>
                      ))}
                    </List>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="success.main">
                  {t("requests.dashboardCard.empty")}
                </Typography>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
      <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
        <Button
          size="small"
          variant="outlined"
          component={RouterLink}
          to={PagePaths.audienceRequests}
        >
          {t("requests.dashboardCard.open")}
        </Button>
      </CardActions>
    </Card>
  );
};
